<script>
import { layoutMethods, puhunanCampaignMethods, puhunanCampaignComputed } from "@/state/helpers";
import moment from "moment";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";

// "All Categories" (code ALL) is a special toggle: it shows every category in the
// table but zeroes the KPI cards. Selecting a specific category switches out of it.
const ALL_CODE = "ALL";
const CATEGORY_OPTIONS = [
  { code: ALL_CODE, label: "All Categories" },
  { code: "AB", label: "App Banner" },
  { code: "PN", label: "Push Notification" },
  { code: "IM", label: "Inbox Message" },
];

// Card definitions — icons/order per design
const CARDS = [
  { key: "clicks", title: "CLICKS", icon: "mdi-gesture-tap" },
  { key: "views", title: "VIEWS", icon: "mdi-eye-check" },
  { key: "impressions", title: "IMPRESSIONS", icon: "mdi-account-box-multiple" },
  { key: "ctr", title: "CTR", icon: "mdi-cursor-default-click", isPercent: true },
  { key: "sent", title: "SENT", icon: "mdi-send" },
  { key: "errors", title: "ERRORS", icon: "mdi-alert-circle" },
];

// Table columns per design (9 columns, horizontally scrollable)
const COLUMNS = [
  { key: "datePosted", label: "Date Posted", width: 203, sortable: true, type: "date" },
  { key: "category", label: "Category", width: 127, sortable: true },
  { key: "campaignName", label: "Campaigned Name", width: 180, sortable: true },
  { key: "clicks", label: "Clicks", width: 123, sortable: true, type: "number" },
  { key: "views", label: "Views", width: 123, sortable: true, type: "number" },
  { key: "impressions", label: "Impressions", width: 143, sortable: true, type: "number" },
  { key: "ctr", label: "CTR", width: 102, sortable: true, type: "percent" },
  { key: "sent", label: "Sent", width: 102, sortable: true, type: "number" },
  { key: "errors", label: "Errors", width: 110, sortable: true, type: "number" },
];

export default {
  page: {
    title: "Peddlr Puhunan CTR",
  },
  components: {
    flatPickr,
  },
  data() {
    return {
      isLoading: false,
      categoryOptions: CATEGORY_OPTIONS,
      cardDefs: CARDS,
      columns: COLUMNS,
      // Default to "All Categories": table shows everything, KPIs read 0.
      selectedCategories: [ALL_CODE],
      // two separate date inputs, per design
      fromDate: moment().subtract(6, "days").format("YYYY-MM-DD"),
      toDate: moment().format("YYYY-MM-DD"),
      singleDateConfig: {
        dateFormat: "Y-m-d",
        altInput: true,
        altFormat: "m/d/Y",
        maxDate: moment().format("YYYY-MM-DD"),
      },
      // applied filter (what the table/cards reflect)
      applied: {
        from: moment().subtract(6, "days").format("YYYY-MM-DD"),
        to: moment().format("YYYY-MM-DD"),
      },
      currentPage: 1,
      perPage: 6,
      pageOptions: [6, 10, 20, 50],
      sortBy: null,
      sortDesc: false,
      isExporting: false,
      hasLoadedOnce: false,
    };
  },
  computed: {
    ...puhunanCampaignComputed,

    cardList() {
      const c = this.ctrCards || {};
      return this.cardDefs.map((def) => ({ ...def, data: c[def.key] }));
    },
    isAllCategories() {
      return this.selectedCategories.includes(ALL_CODE);
    },
    // Payload for the API: "All Categories" -> allCategories flag with no specific
    // codes (KPIs zeroed server-side); otherwise the chosen category codes.
    categoryPayload() {
      return this.isAllCategories
        ? { allCategories: true, categories: [] }
        : { allCategories: false, categories: this.selectedCategories };
    },
    totalRecords() {
      return this.ctrPageInfo?.total || 0;
    },
    totalPages() {
      return Math.max(this.ctrPageInfo?.noOfPage || 1, 1);
    },
    // client-side sort of the current page (server paginates)
    sortedRows() {
      const rows = [...(this.ctrRows || [])];
      if (!this.sortBy) return rows;
      const col = this.columns.find((c) => c.key === this.sortBy);
      const dir = this.sortDesc ? -1 : 1;
      return rows.sort((a, b) => {
        let av = a[this.sortBy];
        let bv = b[this.sortBy];
        if (col?.type === "date") {
          av = moment(av).valueOf() || 0;
          bv = moment(bv).valueOf() || 0;
        } else if (col?.type === "number" || col?.type === "percent") {
          av = Number(av) || 0;
          bv = Number(bv) || 0;
        } else {
          av = String(av ?? "").toLowerCase();
          bv = String(bv ?? "").toLowerCase();
        }
        if (av < bv) return -1 * dir;
        if (av > bv) return 1 * dir;
        return 0;
      });
    },
    // page numbers with ellipsis, per design ( 1 2 3 4 … )
    pageList() {
      const total = this.totalPages;
      const current = this.currentPage;
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
      if (current <= 3) return [1, 2, 3, 4, "...", total];
      if (current >= total - 2) return [1, "...", total - 3, total - 2, total - 1, total];
      return [1, "...", current - 1, current, current + 1, "...", total];
    },
  },
  methods: {
    ...layoutMethods,
    ...puhunanCampaignMethods,

    formatNumber(value) {
      if (value === null || value === undefined || value === "") return "0";
      return new Intl.NumberFormat("en-US").format(value);
    },
    formatCardValue(card) {
      const v = card.data?.value ?? 0;
      return card.isPercent ? `${Number(v).toFixed(2)}%` : this.formatNumber(v);
    },
    cellValue(row, col) {
      const v = row[col.key];
      if (col.type === "number") return this.formatNumber(v);
      if (col.type === "percent") return `${Number(v || 0).toFixed(2)}%`;
      if (col.type === "date") {
        if (!v) return "--";
        const m = moment(v);
        return m.isValid() ? m.format("MMMM D, YYYY [at] hh:mm A") : v;
      }
      return v || "--";
    },
    toggleCategory(code) {
      // "All Categories" is mutually exclusive with specific categories.
      if (code === ALL_CODE) {
        this.selectedCategories = [ALL_CODE];
        return;
      }

      // Selecting a specific category drops "All Categories" first.
      const specific = this.selectedCategories.filter((c) => c !== ALL_CODE);
      const idx = specific.indexOf(code);
      if (idx === -1) specific.push(code);
      else specific.splice(idx, 1);

      // Nothing specific left -> fall back to "All Categories".
      this.selectedCategories = specific.length ? specific : [ALL_CODE];
    },
    isCategorySelected(code) {
      return this.selectedCategories.includes(code);
    },
    toggleSort(col) {
      if (!col.sortable) return;
      if (this.sortBy === col.key) this.sortDesc = !this.sortDesc;
      else {
        this.sortBy = col.key;
        this.sortDesc = false;
      }
    },

    async fetchReport() {
      this.isLoading = true;
      try {
        const res = await this.getPuhunanCTRReport({
          from: this.applied.from,
          to: this.applied.to,
          ...this.categoryPayload,
          page: this.currentPage,
          rows: this.perPage,
        });
        if (res && res.status !== "ok") {
          Swal.fire({ icon: "error", title: "Unable to load report", text: res.message });
        }
      } catch (error) {
        console.error("Failed to load Puhunan CTR report:", error);
        Swal.fire({ icon: "error", title: "Unable to load report", text: "Please try again." });
      } finally {
        this.isLoading = false;
        this.hasLoadedOnce = true;
      }
    },
    applyFilter() {
      this.applied.from = this.fromDate;
      this.applied.to = this.toDate;
      this.currentPage = 1;
      this.fetchReport();
    },
    onPerPageChange() {
      this.currentPage = 1;
      this.fetchReport();
    },
    goToPage(page) {
      if (page === "..." || page < 1 || page > this.totalPages || page === this.currentPage) return;
      this.currentPage = page;
      this.fetchReport();
    },
    // Exports the full filtered set (all pages) — the API emails an xlsx link.
    async onExport() {
      if (this.isExporting) return;
      if (!this.totalRecords) {
        Swal.fire({ icon: "info", title: "Nothing to export", text: "No records for the selected filters." });
        return;
      }

      this.isExporting = true;
      try {
        const res = await this.exportPuhunanCTRReport({
          from: this.applied.from,
          to: this.applied.to,
          ...this.categoryPayload,
        });
        if (res && res.status === "ok") {
          Swal.fire({ icon: "success", title: "Export started", text: res.message });
        } else {
          Swal.fire({ icon: "error", title: "Export failed", text: (res && res.message) || "Please try again." });
        }
      } catch (error) {
        console.error("Failed to export Puhunan CTR report:", error);
        Swal.fire({ icon: "error", title: "Export failed", text: "Please try again." });
      } finally {
        this.isExporting = false;
      }
    },
  },
  watch: {
    selectedCategories() {
      this.currentPage = 1;
      this.fetchReport();
    },
  },
  async mounted() {
    this.changeTitle({ title: "Peddlr Puhunan CTR" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr" },
        { text: "Reports" },
        { text: "Peddlr Puhunan CTR" },
      ],
    });
    await this.fetchReport();
  },
};
</script>

<template>
  <div class="container-fluid ctr-page">
    <!-- Filter Card -->
    <div class="ctr-card mb-3">
      <div class="ctr-filter-row">
        <div class="d-flex align-items-center gap-2 flex-shrink-0">
          <i class="mdi mdi-calendar-blank-outline ctr-filter-icon"></i>
          <span class="ctr-filter-label">DATE RANGE</span>
        </div>

        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="ctr-date-input">
            <flat-pickr v-model="fromDate" :config="singleDateConfig" class="ctr-date-field" placeholder="mm/dd/yyyy" />
            <i class="mdi mdi-calendar-month-outline ctr-date-icon"></i>
          </div>
          <span class="ctr-dash">&mdash;</span>
          <div class="ctr-date-input">
            <flat-pickr v-model="toDate" :config="singleDateConfig" class="ctr-date-field" placeholder="mm/dd/yyyy" />
            <i class="mdi mdi-calendar-month-outline ctr-date-icon"></i>
          </div>
        </div>

        <button class="ctr-btn-apply ms-auto" :disabled="isLoading" @click="applyFilter">Apply Filter</button>
      </div>

      <div class="ctr-divider"></div>

      <div class="d-flex align-items-center gap-2 flex-wrap">
        <span class="ctr-category-label">Select Category:</span>
        <button
          v-for="opt in categoryOptions"
          :key="opt.code"
          type="button"
          :class="['ctr-chip', { 'ctr-chip-active': isCategorySelected(opt.code) }]"
          :aria-pressed="isCategorySelected(opt.code) ? 'true' : 'false'"
          @click="toggleCategory(opt.code)"
        >
          <span class="ctr-chip-box">
            <svg v-if="isCategorySelected(opt.code)" width="8" height="8" viewBox="0 0 10 10" fill="none" aria-hidden="true">
              <path d="M8.2 2.6 3.9 6.9 1.8 4.8" stroke="#7F4697" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </span>
          {{ opt.label }}
        </button>
      </div>
      <p class="ctr-category-note mb-0">Note: To display the total computation, select exactly one category.</p>
    </div>

    <!-- Metric Cards -->
    <div class="ctr-bento mb-3">
      <div v-for="card in cardList" :key="card.key" class="ctr-card ctr-metric">
        <div class="ctr-metric-head">
          <span class="ctr-metric-title">{{ card.title }}</span>
          <i :class="['mdi', card.icon, 'ctr-metric-icon']" aria-hidden="true"></i>
        </div>
        <div class="ctr-metric-value">
          <span v-if="isLoading && !hasLoadedOnce" class="ctr-skeleton"></span>
          <template v-else>{{ formatCardValue(card) }}</template>
        </div>
      </div>
    </div>

    <!-- Records -->
    <div class="ctr-card">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h5 class="ctr-records-count mb-0">{{ formatNumber(totalRecords) }} Record(s)</h5>
        <button class="ctr-btn-export" :disabled="isExporting || isLoading" @click="onExport">
          <i :class="isExporting ? 'bx bx-loader-alt bx-spin' : 'bx bx-upload'"></i>
          <span>{{ isExporting ? 'Exporting…' : 'Export' }}</span>
        </button>
      </div>

      <div class="ctr-table-scroll">
        <table class="ctr-table">
          <thead>
            <tr>
              <th
                v-for="col in columns"
                :key="col.key"
                :style="{ minWidth: col.width + 'px' }"
                :class="{ 'is-sortable': col.sortable }"
                :aria-sort="sortBy === col.key ? (sortDesc ? 'descending' : 'ascending') : 'none'"
                @click="toggleSort(col)"
              >
                <span class="ctr-th-inner">
                  <span>{{ col.label }}</span>
                  <svg v-if="col.sortable" width="10" height="13" viewBox="0 0 10 13" fill="none" aria-hidden="true" class="ctr-sort-icon">
                    <path d="M5 0.5 8 4H2L5 0.5Z" :fill="sortBy === col.key && !sortDesc ? '#7F4697' : '#AFAFAF'"/>
                    <path d="M5 12.5 2 9h6l-3 3.5Z" :fill="sortBy === col.key && sortDesc ? '#7F4697' : '#AFAFAF'"/>
                  </svg>
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="isLoading">
              <td :colspan="columns.length" class="ctr-state">
                <i class="bx bx-loader-alt bx-spin me-2"></i>Loading report…
              </td>
            </tr>
            <tr v-else-if="!sortedRows.length">
              <td :colspan="columns.length" class="ctr-state">
                <i class="mdi mdi-file-search-outline me-2"></i>
                No campaigns found for this date range and category selection.
              </td>
            </tr>
            <template v-else>
              <tr v-for="(row, i) in sortedRows" :key="i">
                <td v-for="col in columns" :key="col.key" :style="{ minWidth: col.width + 'px' }">
                  {{ cellValue(row, col) }}
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Footer: display size + pagination -->
      <div class="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-2">
          <span class="ctr-display-label">Display</span>
          <select v-model.number="perPage" class="ctr-display-select" @change="onPerPageChange" aria-label="Rows per page">
            <option v-for="opt in pageOptions" :key="opt" :value="opt">{{ opt }}</option>
          </select>
        </div>

        <nav v-if="totalPages > 1" class="ctr-pagination" aria-label="Pagination">
          <button class="ctr-page-btn" :disabled="currentPage === 1" @click="goToPage(1)" aria-label="First page">&laquo;</button>
          <button class="ctr-page-btn" :disabled="currentPage === 1" @click="goToPage(currentPage - 1)" aria-label="Previous page">&lsaquo;</button>
          <button
            v-for="(p, idx) in pageList"
            :key="idx"
            :class="['ctr-page-btn', { 'is-active': p === currentPage, 'is-gap': p === '...' }]"
            :disabled="p === '...'"
            @click="goToPage(p)"
          >
            {{ p }}
          </button>
          <button class="ctr-page-btn" :disabled="currentPage === totalPages" @click="goToPage(currentPage + 1)" aria-label="Next page">&rsaquo;</button>
          <button class="ctr-page-btn" :disabled="currentPage === totalPages" @click="goToPage(totalPages)" aria-label="Last page">&raquo;</button>
        </nav>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Design tokens from Figma
   primary #7F4697 | secondary #E83092 | icons (secondary-700) #A52268
   value #441364 | label #4C444F | up #16A34A | down #BA1A1A
   card border #E5E1E7 | input border #CFC3D1 | bg-tertiary #E6EAF1 */

.ctr-page {
  --ctr-primary: #7f4697;
  --ctr-secondary: #e83092;
  --ctr-icon: #a52268;
  --ctr-value: #441364;
  --ctr-label: #4c444f;
  --ctr-card-border: #e5e1e7;
  --ctr-input-border: #cfc3d1;
  --ctr-tertiary: #e6eaf1;
}

/* Card shell — matches Figma: white, 1px #E5E1E7, radius 12, padding 17, subtle shadow */
.ctr-card {
  background: #fff;
  border: 1px solid var(--ctr-card-border);
  border-radius: 12px;
  padding: 17px;
  filter: drop-shadow(0px 1px 1px rgba(0, 0, 0, 0.05));
}

/* ---------- Filter bar ---------- */
.ctr-filter-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.ctr-filter-icon {
  font-size: 18px;
  color: var(--ctr-label);
  line-height: 1;
}

.ctr-filter-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ctr-label);
  letter-spacing: 0.6px;
  line-height: 14.4px;
  text-transform: uppercase;
  white-space: nowrap;
}

.ctr-date-input {
  position: relative;
  display: inline-flex;
  align-items: center;
}

.ctr-date-input ::v-deep input,
.ctr-date-input ::v-deep .flatpickr-input {
  background: #fff;
  border: 1px solid var(--ctr-input-border);
  border-radius: 4px;
  padding: 9px 40px 9px 17px;
  font-size: 14px;
  line-height: 21px;
  color: #1c1b1f;
  width: 172px;
  cursor: pointer;
}

.ctr-date-input ::v-deep input:focus {
  outline: none;
  border-color: var(--ctr-primary);
  box-shadow: 0 0 0 0.15rem rgba(127, 70, 151, 0.12);
}

.ctr-date-icon {
  position: absolute;
  right: 14px;
  font-size: 15px;
  color: var(--ctr-label);
  pointer-events: none;
}

.ctr-dash {
  font-size: 16px;
  line-height: 24px;
  color: var(--ctr-label);
}

.ctr-btn-apply {
  background: var(--ctr-primary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 9px 16px;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: 0.5px;
  line-height: 14.4px;
}
.ctr-btn-apply:hover:not(:disabled) { background: #6c3b81; }
.ctr-btn-apply:disabled { opacity: 0.65; }

.ctr-divider {
  border-top: 1px solid var(--ctr-card-border);
  margin: 17px 0;
}

.ctr-category-label {
  font-size: 12px;
  color: var(--ctr-label);
  margin-right: 4px;
}

.ctr-category-note {
  font-size: 12px;
  color: var(--ctr-label);
  margin-top: 10px;
}

/* Category chips — active: filled primary; inactive: outlined */
.ctr-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid var(--ctr-primary);
  background: #fff;
  color: var(--ctr-primary);
  font-size: 10px;
  font-weight: 400;
  line-height: 15px;
}

.ctr-chip-active {
  background: var(--ctr-primary);
  color: #fff;
}

.ctr-chip-box {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: #fff;
  border: 1px solid var(--ctr-primary);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ctr-chip-active .ctr-chip-box { border-color: #fff; }

/* ---------- Metric cards: 3 per row ---------- */
.ctr-bento {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}

.ctr-metric {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ctr-metric-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 28px;
}

.ctr-metric-title {
  font-size: 12px;
  font-weight: 500;
  color: var(--ctr-label);
  letter-spacing: 0.6px;
  line-height: 14.4px;
  text-transform: uppercase;
}

.ctr-metric-icon {
  font-size: 24px;
  line-height: 24px;
  color: var(--ctr-icon);
}

.ctr-metric-value {
  font-size: 24px;
  font-weight: 600;
  line-height: 31.2px;
  color: var(--ctr-value);
}

/* ---------- Records header ---------- */
.ctr-records-count {
  font-family: 'Roboto', sans-serif;
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0.3px;
  color: #000;
}

.ctr-btn-export {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: var(--ctr-secondary);
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 10px;
  min-width: 105px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.21px;
}
.ctr-btn-export:hover { background: #d02782; }
.ctr-btn-export i { font-size: 19px; line-height: 1; }

/* ---------- Table ---------- */
/* Horizontal scroll with a slim rounded bar, matching the design's thin track */
.ctr-table-scroll {
  overflow-x: auto;
  padding-bottom: 4px;
  scrollbar-width: thin;
  scrollbar-color: #c9c9c9 #f1f1f1;
}
.ctr-table-scroll::-webkit-scrollbar { height: 6px; }
.ctr-table-scroll::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
.ctr-table-scroll::-webkit-scrollbar-thumb {
  background: #c9c9c9;
  border-radius: 3px;
}
.ctr-table-scroll::-webkit-scrollbar-thumb:hover { background: #b0b0b0; }

/* auto layout + per-column min-widths: fills the card on wide screens,
   scrolls instead of squashing on narrow ones */
.ctr-table {
  width: 100%;
  border-collapse: collapse;
  font-family: 'Roboto', sans-serif;
}

.ctr-table th {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12px;
  color: #000;
  padding: 10px 20px;
  text-align: left;
  border-bottom: 1px solid var(--ctr-tertiary);
  white-space: nowrap;
  background: #fff;
}

.ctr-table th.is-sortable { cursor: pointer; user-select: none; }

.ctr-th-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.ctr-sort-icon { flex-shrink: 0; opacity: 0.9; }
.ctr-table th.is-sortable:hover .ctr-sort-icon { opacity: 1; }
.ctr-table th.is-sortable:hover { background: #faf8fb; }

.ctr-table td {
  font-size: 12px;
  font-weight: 400;
  letter-spacing: 0.12px;
  color: #000;
  padding: 20px;
  text-align: left;
  border-bottom: 1px solid var(--ctr-tertiary);
  white-space: nowrap;
}

/* Row hover aids scanning a wide, horizontally-scrolling table */
.ctr-table tbody tr:hover td { background: #faf8fb; }

/* Loading / empty states */
.ctr-state {
  color: var(--ctr-label);
  font-size: 13px;
  text-align: center;
  padding: 40px 20px !important;
}

.ctr-skeleton {
  display: inline-block;
  width: 72px;
  height: 24px;
  border-radius: 4px;
  background: linear-gradient(90deg, #f0ecf2 25%, #e6e0ea 37%, #f0ecf2 63%);
  background-size: 400% 100%;
  animation: ctr-shimmer 1.2s ease-in-out infinite;
}

@keyframes ctr-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0 50%; }
}

.ctr-btn-export:disabled { opacity: 0.7; cursor: not-allowed; }

/* ---------- Footer ---------- */
.ctr-display-label {
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12px;
  color: #000;
}

.ctr-display-select {
  height: 22px;
  width: 46px;
  padding: 0 5px;
  border: 1px solid var(--ctr-tertiary);
  border-radius: 4px;
  background: #fff;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12px;
  color: #000;
}

.ctr-pagination {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.ctr-page-btn {
  min-width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  border-radius: 50%;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  color: #4b4b4b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.ctr-page-btn:hover:not(:disabled):not(.is-active) { background: #f5f2f7; }
.ctr-page-btn.is-active {
  background: var(--ctr-primary);
  color: #fff;
  font-weight: 500;
}
.ctr-page-btn:disabled { color: #afafaf; }
.ctr-page-btn.is-gap { pointer-events: none; }

@media (max-width: 991px) {
  .ctr-bento { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .ctr-bento { grid-template-columns: 1fr; }
}
</style>
